import React, { useEffect, useRef, useState } from 'react';
import PdfIcon from '@/icons/files/PdfIcon2';
import TxtIcon from '@/icons/files/TxtIcon';
import DocIcon from '@/icons/files/DocIcon';
import DocxIcon from '@/icons/files/DocxIcon';
import WebpIcon from '@/icons/files/WebpIcon';
import PngIcon from '@/icons/files/PngIcon';
import JpgIcon from '@/icons/files/JpgIcon';
import JpegIcon from '@/icons/files/JpegIcon';
import { DownloadIcon } from '../../../svgs/svgs';
import { getFileExtension } from '../../../utils/helpers/helpers';
import AttachmentService, {
  Attachment,
  AttachmentParent,
} from '@/services/AttachmentService';
import { useToast } from '@/context/ToastContext';

const fileIcons: Record<string, () => React.ReactElement> = {
  pdf: PdfIcon,
  txt: TxtIcon,
  doc: DocIcon,
  docx: DocxIcon,
  jpg: JpgIcon,
  jpeg: JpegIcon,
  png: PngIcon,
  webp: WebpIcon,
};

const isLikelyCorsError = (err: unknown): boolean => {
  const msg = (err as any)?.message?.toString().toLowerCase() ?? '';
  return (
    msg.includes('network error') ||
    msg.includes('cors') ||
    (err as any)?.code === 'ERR_NETWORK'
  );
};

interface Props {
  parent: AttachmentParent;
}

/**
 * Read-only list of attachments belonging to a single comment. Fetches on
 * mount via the comment-scoped GET endpoint and renders compact download chips.
 */
export default function CommentAttachmentsList({ parent }: Props) {
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());
  const { addToast } = useToast();
  const parentKey = JSON.stringify(parent);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    let cancelled = false;
    (async () => {
      try {
        const list = await AttachmentService.listAttachments(parent);
        if (!cancelled) setAttachments(list);
      } catch (err) {
        // Silent: a missing list shouldn't break comment rendering.
        console.error('Failed to load comment attachments', err);
      }
    })();
    return () => {
      cancelled = true;
      mountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentKey]);

  const handleDownload = async (att: Attachment) => {
    if (busyIds.has(att._id)) return;
    setBusyIds((prev) => new Set(prev).add(att._id));
    try {
      await AttachmentService.downloadAttachment(att._id, att.original_name);
    } catch (err) {
      console.error('Download failed', err);
      addToast(
        isLikelyCorsError(err)
          ? `Couldn't download "${att.original_name}". File delivery is misconfigured (CloudFront CORS). Please contact support.`
          : `Couldn't download "${att.original_name}". Please try again.`,
        'error',
        5000
      );
    } finally {
      setBusyIds((prev) => {
        const next = new Set(prev);
        next.delete(att._id);
        return next;
      });
    }
  };

  if (attachments.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2 mt-2">
      {attachments.map((att) => {
        const ext = getFileExtension(att.original_name);
        const IconComponent = fileIcons[ext];
        const busy = busyIds.has(att._id);
        return (
          <button
            key={att._id}
            type="button"
            onClick={() => handleDownload(att)}
            disabled={busy}
            title={att.display_name || att.original_name}
            className="flex items-center gap-2 px-2 py-1.5 bg-[#141414] rounded-md min-w-0 max-w-full hover:bg-black-7 transition-colors disabled:opacity-50"
          >
            {IconComponent && (
              <div className="flex-shrink-0">
                <IconComponent />
              </div>
            )}
            <span className="text-xs text-text-primary-body truncate max-w-[160px]">
              {att.display_name || att.original_name}
            </span>
            <div className="w-3 flex-shrink-0">
              <DownloadIcon />
            </div>
          </button>
        );
      })}
    </div>
  );
}
